/**
 * useNarrationAlignment — fetches word-level timing data for a chain's
 * narration audio and exposes a lookup that maps audio currentTime to
 * the active word index.
 *
 * Apr 30 2026 — Lead's call: "very precisely following my voice." Per
 * the rebake script, MongoDB's media_files row carries a 2,180-entry
 * `alignment_words` array and a `voice_text` string that mirrors the
 * audio script (citations stripped, whitespace normalized). The chain
 * page hits /api/narration/chain/<id>/<step>/alignment to fetch them.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import api from './api';

export function useNarrationAlignment(chainId, stepKey = 'full') {
  const [voiceText, setVoiceText] = useState('');
  const [words, setWords] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!chainId) return;
    let cancelled = false;
    api.get(`/narration/chain/${chainId}/${stepKey}/alignment`)
      .then((r) => {
        if (cancelled) return;
        setVoiceText(r.data.voice_text || '');
        setWords(Array.isArray(r.data.words) ? r.data.words : []);
      })
      .catch((e) => { if (!cancelled) setError(e); });
    return () => { cancelled = true; };
  }, [chainId, stepKey]);

  // Build per-word char ranges within voiceText so we can map a voice
  // word index to its absolute char position. The bake script joins
  // words by single spaces inside `voice_text`, so we just walk the
  // string and track positions.
  const wordRanges = useMemo(() => {
    if (!voiceText || !words.length) return [];
    const out = [];
    let cursor = 0;
    for (const w of words) {
      const idx = voiceText.indexOf(w.w, cursor);
      if (idx < 0) {
        // Drift recovery — push a sentinel so indexes still align.
        out.push({ start: cursor, end: cursor + w.w.length, ts: w });
        cursor += w.w.length + 1;
      } else {
        out.push({ start: idx, end: idx + w.w.length, ts: w });
        cursor = idx + w.w.length;
      }
    }
    return out;
  }, [voiceText, words]);

  return { voiceText, words, wordRanges, ready: words.length > 0, error };
}

export function findActiveWordIdx(words, currentTime) {
  if (!words || !words.length) return -1;
  // Binary search — words are time-monotonic.
  let lo = 0, hi = words.length - 1, ans = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const w = words[mid];
    if (currentTime >= w.s && currentTime < w.e) return mid;
    if (currentTime < w.s) hi = mid - 1;
    else { ans = mid; lo = mid + 1; }
  }
  return ans;
}

/**
 * Match a displayed prose body to the global voice text by walking both
 * char-by-char, skipping `[N]` cite markers in the displayed prose. Returns
 * a parallel array of length displayed.length where each char has its
 * matching voice-word index (or -1 if it doesn't map to one).
 */
export function buildBodyToWordMap(displayed, voiceText, voiceCharOffset, wordRanges) {
  if (!displayed) return [];
  const map = new Array(displayed.length).fill(-1);
  let v = voiceCharOffset;
  for (let d = 0; d < displayed.length; d++) {
    const ch = displayed[d];
    if (ch === '[') {
      // Skip [N] or [S_TAG] — these don't appear in voice_text.
      const close = displayed.indexOf(']', d);
      if (close > d) { d = close; continue; }
    }
    // Skip whitespace runs in displayed but advance v for one space.
    if (/\s/.test(ch)) {
      // Voice text uses single spaces / runs of `. , .` — advance v
      // through any whitespace and section separators.
      while (v < voiceText.length && /[\s.,]/.test(voiceText[v]) && (voiceText[v] !== '.' || /[\s.,]/.test(voiceText[v + 1] || ' '))) {
        v++;
      }
      continue;
    }
    // Find the voice char that matches displayed ch (advance through
    // any leftover whitespace/punctuation in voice text first).
    while (v < voiceText.length && voiceText[v] !== ch) v++;
    if (v >= voiceText.length) {
      // Drifted past the end — leave as -1 from here on.
      break;
    }
    // Find which word range this voice char belongs to.
    // Use linear scan with a memoised cursor for speed.
    if (typeof buildBodyToWordMap._cursor !== 'number' || buildBodyToWordMap._cursor < 0) {
      buildBodyToWordMap._cursor = 0;
    }
    let wi = buildBodyToWordMap._cursor;
    while (wi < wordRanges.length && wordRanges[wi].end <= v) wi++;
    if (wi >= wordRanges.length) { v++; continue; }
    if (v >= wordRanges[wi].start && v < wordRanges[wi].end) {
      map[d] = wi;
    }
    buildBodyToWordMap._cursor = wi;
    v++;
  }
  return map;
}
